import {
  faChevronDown,
  faDownload,
  faEye,
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
  faUndo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import EllipsisPopover from '../../Shared/MaterialPopover';
import WebViewerModal from '../../Shared/PDFEditor/WebViewerModal';
import AssetUploadInput from './AssetUploadInput';
import uploadApi from '../../../utils/api/upload';
import { AssetTypeField } from '../../../utils/api/asset';
import {
  AssetUpload,
  ProjectAsset,
  projectAssetsApi,
} from '../../../utils/api/projectAsset';
import AssetChecks from './AssetChecks';
import AssetPhotos, { isPhoto } from './AssetPhotos';
import AssetFindings from './AssetFindings';

const NOTE_FIELD_ID = null;
// prettier-ignore
const fmtDate = (d?: string | null) =>
  d ? dayjs(d).format('MMMM D, YYYY') : '';

const isPdf = (u: AssetUpload) =>
  u.upload?.mime_type === 'application/pdf' ||
  !!u.upload?.name?.toLowerCase().endsWith('.pdf');

interface IAssetCardProps {
  projectId: number;
  asset: ProjectAsset;
  inspectionId?: number;
  canEdit: boolean;
  /** Project-level management (edit/archive/restore). Omitted in inspections. */
  onEdit?: (asset: ProjectAsset) => void;
  onArchive?: (asset: ProjectAsset) => void;
  onRestore?: (asset: ProjectAsset) => void;
  onChanged: () => void;
}

const AssetCard: React.FC<IAssetCardProps> = ({
  projectId,
  asset,
  inspectionId,
  canEdit,
  onEdit,
  onArchive,
  onRestore,
  onChanged,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [noteValue, setNoteValue] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);
  const [lastNote, setLastNote] = useState<{
    inspection_date: string | null;
    notes: string | null;
  } | null>(null);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pdfDoc, setPdfDoc] = useState<{
    doc: AssetUpload;
    download?: boolean;
  } | null>(null);

  const isArchived = !!asset.archived_at;
  const editable = canEdit && !isArchived;
  const type = asset.asset_type;
  const color = type?.featureable_data?.config?.color || '#6B7280';
  const fields = useMemo(() => type?.fields || [], [type]);
  const staticFields = fields.filter((f) => !f.is_inspectable && f.is_active);
  const inspectableFields = fields.filter(
    (f) => f.is_inspectable && f.is_active,
  );
  const photos = (asset.uploads || []).filter(isPhoto);
  const documents = (asset.uploads || []).filter((u) => !isPhoto(u));

  const staticValue = (id?: number) =>
    asset.field_answers?.find((a) => a.asset_field_type_id === id)?.value || '';
  const checkValue = (id?: number) =>
    asset.inspection_answers?.find((a) => a.asset_field_type_id === id)
      ?.value || '';
  const inspectionNote =
    asset.inspection_answers?.find((a) => a.asset_field_type_id == null)
      ?.value || '';

  // Checks status: required inspectable fields that are all answered = complete.
  const requiredChecks = inspectableFields.filter((f) => f.is_required);
  const checksComplete = requiredChecks.every(
    (f) => checkValue(f.id).trim() !== '',
  );
  const showChecksBadge = !!inspectionId && inspectableFields.length > 0;

  useEffect(() => {
    setNoteValue(inspectionNote);
  }, [inspectionNote]);

  useEffect(() => {
    if (inspectionId) return;
    projectAssetsApi
      .lastNote(projectId, asset.id)
      .then(setLastNote)
      .catch(() => {});
  }, [projectId, asset.id, inspectionId]);

  const downloadUrl = (u: AssetUpload) =>
    u.upload?.GUID
      ? uploadApi.getDownloadUrl(u.upload.GUID, null, false, Date.now())
      : '';

  const handleUpload = async (uploadRecord: {
    id: number;
    mime_type?: string;
  }) => {
    try {
      await projectAssetsApi.addUpload(projectId, asset.id, {
        upload_id: uploadRecord.id,
        inspection_id: inspectionId ?? null,
        upload_type: uploadRecord.mime_type?.startsWith('image/')
          ? 'photo'
          : 'document',
      });
      onChanged();
    } catch {
      toast.error('Failed to attach file.');
    }
  };

  const handleRemoveUpload = async (u: AssetUpload) => {
    try {
      await projectAssetsApi.removeUpload(projectId, asset.id, u.id);
      onChanged();
    } catch {
      toast.error('Failed to remove file.');
    }
  };

  const saveCheck = async (field: AssetTypeField, value: string) => {
    if (!inspectionId) return;
    try {
      await projectAssetsApi.saveInspectionAnswer(projectId, asset.id, {
        inspection_id: inspectionId,
        asset_field_type_id: field.id,
        value,
      });
      toast.success(`Saved check for ${field.label}.`);
      onChanged();
    } catch {
      toast.error('Failed to save check.');
    }
  };

  const saveNote = async (value: string) => {
    if (!inspectionId) return;
    try {
      await projectAssetsApi.saveInspectionAnswer(projectId, asset.id, {
        inspection_id: inspectionId,
        asset_field_type_id: NOTE_FIELD_ID,
        value,
      });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1500);
    } catch {
      toast.error('Failed to save inspection note.');
    }
  };

  const onNoteChange = (value: string) => {
    setNoteValue(value);
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => saveNote(value), 600);
  };

  // Management actions only exist at the project level (handlers provided).
  const canManage = isArchived ? !!onRestore : !!(onEdit && onArchive);
  const popoverOptions = isArchived
    ? [
        {
          icon: faUndo,
          label: 'Restore Asset',
          onClickHandler: () => onRestore?.(asset),
        },
      ]
    : [
        {
          icon: faPencilAlt,
          label: 'Edit Asset',
          onClickHandler: () => onEdit?.(asset),
        },
        {
          icon: faTrashAlt,
          label: 'Archive Asset',
          onClickHandler: () => onArchive?.(asset),
        },
      ];

  return (
    <section
      className={classNames('asset-section', {
        'is-archived': isArchived,
        'is-collapsed': collapsed,
      })}
      style={{ borderLeftColor: color }}
    >
      <div className='asset-section__head'>
        <span
          className='asset-section__badge'
          style={{ backgroundColor: color }}
        >
          {type?.code}
        </span>
        <div className='asset-section__title'>
          <span className='asset-section__name'>{asset.asset_name}</span>
          {type?.name && (
            <span className='asset-section__type'>{type.name}</span>
          )}
        </div>
        {isArchived && (
          <span className='asset-section__archived-badge'>Archived</span>
        )}
        <span className='asset-section__spacer' />
        {asset.asset_status && (
          <span
            className='asset-section__status'
            style={{
              backgroundColor: `${asset.asset_status.color || '#9CA3AF'}1d`,
              color: asset.asset_status.color || '#6B7280',
            }}
          >
            {asset.asset_status.name}
          </span>
        )}
        {showChecksBadge && (
          <span
            className={classNames('asset-section__checks-badge', {
              'is-complete': checksComplete,
              'is-pending': !checksComplete,
            })}
          >
            {checksComplete ? 'Checks Complete' : 'Checks Pending'}
          </span>
        )}
        {canEdit && canManage && (
          <EllipsisPopover
            aria-label={`Actions for ${asset.asset_name}`}
            options={popoverOptions}
          />
        )}
        <button
          className='asset-section__collapse'
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          onClick={() => setCollapsed((c) => !c)}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>
      </div>

      {!collapsed && (
        <div className='asset-section__body'>
          {/* Fields + Documents */}
          <div className='asset-section__two-col'>
            <div>
              <h5>Fields</h5>
              {staticFields.length === 0 ? (
                <p className='asset-section__empty'>No fields defined.</p>
              ) : (
                <dl className='asset-section__fields'>
                  {staticFields.map((f) => (
                    <div key={f.id}>
                      <dt>{f.label}</dt>
                      <dd className={staticValue(f.id) ? '' : 'is-blank'}>
                        {staticValue(f.id) || '—'}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            <div>
              <div className='asset-section__col-head'>
                <h5>Documents</h5>
                {editable && (
                  <AssetUploadInput
                    onUpload={handleUpload}
                    imageDimensions={{
                      width: 20,
                      height: 20,
                    }}
                    allowedTypes={[
                      'image/jpg',
                      'image/jpeg',
                      'image/png',
                      'application/pdf',
                    ]}
                    className='asset-section__upload'
                    maxFileSize={75000000}
                  />
                )}
              </div>
              {documents.length === 0 ? (
                <p className='asset-section__empty'>No documents.</p>
              ) : (
                <ul className='asset-section__docs'>
                  {documents.map((d) => (
                    <li key={d.id}>
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        className='asset-section__doc-icon'
                      />
                      <span className='asset-section__doc-name'>
                        {d.upload?.name || 'Document'}
                      </span>
                      <EllipsisPopover
                        aria-label={`Actions for ${d.upload?.name}`}
                        options={[
                          ...(isPdf(d)
                            ? [
                                {
                                  icon: editable ? faPencilAlt : faEye,
                                  label: editable ? 'Edit' : 'View',
                                  onClickHandler: () => setPdfDoc({ doc: d }),
                                },
                              ]
                            : []),
                          {
                            icon: faDownload,
                            label: 'Download',
                            onClickHandler: () =>
                              isPdf(d)
                                ? setPdfDoc({ doc: d, download: true })
                                : window.open(downloadUrl(d), '_blank'),
                          },
                          ...(editable
                            ? [
                                {
                                  icon: faTrashAlt,
                                  label: 'Delete',
                                  onClickHandler: () => handleRemoveUpload(d),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Inspection checks (inspection context only) */}
          {inspectionId && inspectableFields.length > 0 && (
            <AssetChecks
              fields={inspectableFields}
              valueOf={checkValue}
              onSave={saveCheck}
              canEdit={editable}
            />
          )}

          {/* Notes: project | (inspection note or last inspection note) */}
          <div className='asset-section__two-col'>
            <div>
              <h5>Project Note</h5>
              <p
                className={
                  asset.notes ? 'asset-section__note' : 'asset-section__empty'
                }
              >
                {asset.notes || 'No project note.'}
              </p>
            </div>
            <div>
              {inspectionId ? (
                <>
                  <h5>
                    Inspection Note
                    {noteSaved && (
                      <span className='asset-section__saved'>✓ Saved</span>
                    )}
                  </h5>
                  <textarea
                    className='asset-section__note-input'
                    value={noteValue}
                    disabled={!editable}
                    placeholder={
                      editable ? 'What did you observe during this visit?' : ''
                    }
                    onChange={(e) => onNoteChange(e.target.value)}
                    onBlur={(e) => saveNote(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <h5>Last Inspection Note</h5>
                  {lastNote?.notes ? (
                    <>
                      {lastNote.inspection_date && (
                        <p className='asset-section__note-meta'>
                          From {fmtDate(lastNote.inspection_date)}
                        </p>
                      )}
                      <p className='asset-section__note'>{lastNote.notes}</p>
                    </>
                  ) : (
                    <p className='asset-section__empty'>
                      No inspection notes yet.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Photos */}
          <AssetPhotos
            photos={photos}
            canEdit={editable}
            showFilter={!inspectionId}
            downloadUrl={downloadUrl}
            onUpload={handleUpload}
            onRemove={handleRemoveUpload}
          />

          {/* Findings attached to this asset */}
          <AssetFindings
            projectId={projectId}
            asset={asset}
            inspectionId={inspectionId}
            canEdit={editable}
            onChanged={onChanged}
          />
        </div>
      )}

      {/* PDF viewer/editor for asset documents, as on project documents */}
      {pdfDoc?.doc.upload?.GUID && (
        <WebViewerModal
          show
          onClose={() => setPdfDoc(null)}
          onSave={onChanged}
          guid={pdfDoc.doc.upload.GUID}
          id={pdfDoc.doc.upload.id}
          uploadId={pdfDoc.doc.upload.id}
          filename={pdfDoc.doc.upload.name || 'Document'}
          canDownload={pdfDoc.download}
          canEdit={editable}
        />
      )}
    </section>
  );
};

export default AssetCard;
